import { Room, RoomStatus } from '../../../types/models';
import { cn } from '../../../utils/cn';

interface RoomInfoProps {
    room: Room;
    className?: string;
    languageCode?: string;
}

/**
 * Room info component for displaying detailed room information
 *
 * Features:
 * - Room name, status and description
 * - Formatted content with proper spacing
 * - Status badge
 * - Responsive layout
 */
const RoomInfo = ({ room, className, languageCode = 'en' }: RoomInfoProps) => {
    // Get default language translation or first available
    const translation = room.translations.find((t) => t.language_code === languageCode) || room.translations[0];

    // Get status display properties
    const getStatusDisplay = (status: RoomStatus) => {
        switch (status) {
            case RoomStatus.Published:
                return {
                    color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
                    label: 'Published',
                };
            case RoomStatus.Draft:
                return {
                    color: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
                    label: 'Draft',
                };
            case RoomStatus.Archived:
                return {
                    color: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
                    label: 'Archived',
                };
            default:
                return {
                    color: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
                    label: status,
                };
        }
    };

    const statusDisplay = getStatusDisplay(room.status);

    if (!translation) {
        return (
            <div className={cn('rounded-lg bg-red-50 p-4 dark:bg-red-900/20', className)}>
                <p className="text-red-600 dark:text-red-400">No translation information available for this room.</p>
            </div>
        );
    }

    return (
        <div className={cn('space-y-6', className)}>
            {/* Room header with name and status */}
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{translation.name}</h1>

                <span className={cn('rounded-full px-3 py-1 text-sm font-medium', statusDisplay.color)}>{statusDisplay.label}</span>
            </div>

            {/* Room description */}
            {translation.description && (
                <div className="prose dark:prose-invert max-w-none">
                    <p className="whitespace-pre-line text-gray-700 dark:text-gray-300">{translation.description}</p>
                </div>
            )}

            {/* Room metadata */}
            <div className="grid grid-cols-1 gap-4 border-t border-gray-200 pt-6 md:grid-cols-2 dark:border-gray-700">
                {/* Number of exhibits */}
                <div className="flex items-center gap-3">
                    <div className="bg-primary bg-opacity-10 text-primary flex h-10 w-10 items-center justify-center rounded-lg">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <rect width="18" height="18" x="3" y="3" rx="2" ry="2"></rect>
                            <circle cx="9" cy="9" r="2"></circle>
                            <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path>
                        </svg>
                    </div>
                    <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Exhibits</p>
                        <p className="font-medium text-gray-900 dark:text-white">
                            {room.exhibits?.length || 0} {room.exhibits?.length === 1 ? 'Exhibit' : 'Exhibits'}
                        </p>
                    </div>
                </div>

                {/* Last updated */}
                <div className="flex items-center gap-3">
                    <div className="bg-primary bg-opacity-10 text-primary flex h-10 w-10 items-center justify-center rounded-lg">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <circle cx="12" cy="12" r="10"></circle>
                            <polyline points="12 6 12 12 16 14"></polyline>
                        </svg>
                    </div>
                    <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Last Updated</p>
                        <p className="font-medium text-gray-900 dark:text-white">{new Date(room.updated_at).toLocaleDateString()}</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RoomInfo;
